import { Injectable } from '@angular/core';
import {DataService} from "./data.service";
import {take} from "rxjs";
import {ExperienceEntry} from "../interfaces/experience-entry";
import {PlayTimeEntry} from "../interfaces/play-time-entry";

@Injectable({
  providedIn: 'root'
})
export class OrganizationExperienceService {

  private _cache: Map<string, any> = new Map();
  private resetCacheTimeout = -1;
  private readonly RESET_CACHE_TIMEOUT_DELAY = 1000 * 60 * 60 * 5; // 5min
  constructor(private data: DataService) { }


  async loadSchoolClassLatestExperience(classEntryCode: string, forceUpdate: boolean): Promise<number> {

    if(!this.isCacheEmpty(classEntryCode) && !forceUpdate) {

      const cachedData = this._cache.get(classEntryCode);
      return cachedData[0].points;
    }

    const history = await this.getServerExperienceByClassEntryCode(classEntryCode);

    if(history.length) {
      return history[0].points;
    }

    console.warn("Couldn't load experience history...");
    return 0;

  }

  async loadSchoolClassLatestPlayTime(classEntryCode: string, forceUpdate: boolean): Promise<number> {

    if(!this.isCacheEmpty(classEntryCode) && !forceUpdate) {
      const cache = this._cache.get(classEntryCode)
      return cache[0].playtime;
    }

    const history = await this.getServerPlayTimeByClassEntryCode(classEntryCode);
    if(history.length) {
      return history[0].playTime;
    }

    console.warn("Couldn't load playtime history...");
    return 0;

  }

  async loadSchoolClassHistoryExperience(classEntryCode: string): Promise<ExperienceEntry[]> {
    return await this.getServerExperienceByClassEntryCode(classEntryCode);
  }

  async loadSchoolClassHistoryPlayTime(classEntryCode: string): Promise<PlayTimeEntry[]> {
    return await this.getServerPlayTimeByClassEntryCode(classEntryCode);
  }

  private isCacheEmpty(classEntryCode: string): boolean {
    return !this._cache.has(classEntryCode);
  }

  private async getServerExperienceByClassEntryCode(classEntryCode: string): Promise<ExperienceEntry[]> {
    return new Promise((resolve, reject) => {

      if(!classEntryCode) {
        resolve([]);
        return;
      }

      this.getSchoolClassGameHistory(classEntryCode, false).then((val) => {
        if(val && val.length) {
          const mappedData: ExperienceEntry[] = val.map((el: any) => {
            return {
              points: el.points ?? 0,
              date: el.createdAt ?? ""
            }
          })
          resolve(mappedData);
        } else {
          resolve([]);
        }
      }).catch((err) => {
        resolve([])
      })
    })
  }

  private async getServerPlayTimeByClassEntryCode(classEntryCode: string): Promise<PlayTimeEntry[]> {
    return new Promise(async (resolve, reject) => {

      this.getSchoolClassGameHistory(classEntryCode, false).then((val) => {
        // console.log('game-history-length',val.length);
        if(val && val.length) {
          const mappedData: PlayTimeEntry[] = val.map((el: any) => {
            return {
              playTime: el.playtime ?? 0,
              date: el.createdAt ?? ""
            }
          })
          resolve(mappedData);
        } else {
          resolve([]);
        }
      }).catch((err) => {
        resolve([])
      })
    })
  }

  private getSchoolClassGameHistory(classEntryCode: string, forceUpdate: boolean): Promise<any> {
    return new Promise((resolve, reject) => {

      if(!classEntryCode || classEntryCode.length === 0) {
        reject("No class entry code provided");
      }

      const cacheValue = this._cache.get(classEntryCode);
      if(cacheValue && !forceUpdate) {
        resolve(cacheValue);
      } else {
        this.data.getSchoolClassGameHistory(classEntryCode).pipe(take(1)).subscribe((val) => {
          if(val && val.length) {
            this._cache.set(classEntryCode, val);
          }
          resolve(val);
        })
      }

    })
  }
}
